/* Права участника по разделам мини-аппа (настраиваются админом в карточке участника).

   Хранятся в users.access одним JSON:

     {
       overview: "edit" | "read" | "none",   // главная
       finance:  "edit" | "read" | "none",   // финансы
       crm:      "edit" | "read" | "none",   // умолчание для всей CRM
       alerts:   "read" | "none",            // алерты — только чтение по своей природе
       crmBiz: {                             // переопределения по бизнесам
         "<bizId>": "edit" | "read" | "none"           // бизнес целиком
                  | { clients, partners, accounting, dev, tasks } // либо по воронкам
       },
       more: { "<blockId>": "none" }         // скрытые блоки раздела «Ещё»
     }

   Незаполненное наследуется: воронка ← бизнес ← общий уровень CRM ← полный доступ.
   Пустой access означает полный доступ — так ведут себя участники, заведённые
   до появления прав. Администратору разделы открыты всегда. */

export const ACCESS_EDIT = "edit";
export const ACCESS_READ = "read";
export const ACCESS_NONE = "none";

/** Воронки CRM, у которых есть свой уровень доступа (id из streamsOf) */
export const CRM_STREAM_KEYS = ["clients", "partners", "accounting", "dev", "tasks"];

/* Блоки раздела «Ещё»: каждый либо виден участнику, либо нет — уровней у них
   нет, внутри нечего «читать без правки». Профиль, тема и справочники сюда не
   входят: первые два есть у всех, справочники и так открыты только админу. */
export const MORE_BLOCKS = [
  { id: "reports", label: "Отчёты" },
  { id: "tools", label: "Утилиты" },
  { id: "vault", label: "Хранилище доступов" },
  { id: "shortcut", label: "Шорткат" },
];

const MORE_IDS = MORE_BLOCKS.map((b) => b.id);

const LEVELS = new Set([ACCESS_EDIT, ACCESS_READ, ACCESS_NONE]);
const norm = (v, fallback) => (LEVELS.has(v) ? v : fallback);

export const canRead = (level) => level === ACCESS_EDIT || level === ACCESS_READ;
export const canEdit = (level) => level === ACCESS_EDIT;

/** Уровень раздела: overview | finance | crm | alerts */
export function sectionLevel(user, section) {
  if (!user) return ACCESS_NONE;
  if (user.isAdmin) return section === "alerts" ? ACCESS_READ : ACCESS_EDIT;
  const a = user.access || {};
  // Алерты не правят — у них только «показывать/скрыть»
  if (section === "alerts") return norm(a.alerts, ACCESS_READ) === ACCESS_NONE ? ACCESS_NONE : ACCESS_READ;
  if (section === "crm") return crmLevel(user, null);
  return norm(a[section], ACCESS_EDIT);
}

/** Уровень CRM для бизнеса и воронки. Без stream — самый широкий уровень
    по воронкам бизнеса; без bizId — общий уровень CRM. */
export function crmLevel(user, bizId, stream) {
  if (!user) return ACCESS_NONE;
  if (user.isAdmin) return ACCESS_EDIT;
  const a = user.access || {};
  const base = norm(a.crm, ACCESS_EDIT);
  if (!bizId) return base;
  const biz = (a.crmBiz || {})[bizId];
  if (biz == null) return base;
  if (typeof biz === "string") return norm(biz, base);
  if (stream) return norm(biz[stream], base);
  const levels = CRM_STREAM_KEYS.map((s) => norm(biz[s], base));
  return levels.includes(ACCESS_EDIT) ? ACCESS_EDIT
    : levels.includes(ACCESS_READ) ? ACCESS_READ
      : ACCESS_NONE;
}

/** Пускать ли участника в CRM вообще: общий уровень либо хоть одно переопределение */
export function crmAny(user, pred = canRead) {
  if (!user) return false;
  if (user.isAdmin) return true;
  const a = user.access || {};
  if (pred(norm(a.crm, ACCESS_EDIT))) return true;
  return Object.values(a.crmBiz || {}).some((biz) =>
    typeof biz === "string"
      ? pred(norm(biz, ACCESS_NONE))
      : CRM_STREAM_KEYS.some((s) => pred(norm(biz?.[s], ACCESS_NONE)))
  );
}

/** Виден ли участнику блок раздела «Ещё» (id из MORE_BLOCKS) */
export function moreVisible(user, block) {
  if (!user) return false;
  if (user.isAdmin) return true;
  return (user.access?.more || {})[block] !== ACCESS_NONE;
}

/** Право записывать деньги: операции и платежи правят и из финансов, и с главной */
export const moneyEdit = (user) =>
  canEdit(sectionLevel(user, "finance")) || canEdit(sectionLevel(user, "overview"));

/** Настройки для формы участника: каждый уровень выписан явно, ничего не наследуется втихую */
export function accessToForm(raw) {
  const a = raw || {};
  return {
    overview: norm(a.overview, ACCESS_EDIT),
    finance: norm(a.finance, ACCESS_EDIT),
    crm: norm(a.crm, ACCESS_EDIT),
    alerts: norm(a.alerts, ACCESS_READ) === ACCESS_NONE ? ACCESS_NONE : ACCESS_READ,
    crmBiz: Object.fromEntries(
      Object.entries(a.crmBiz || {}).map(([id, v]) => [id, typeof v === "string" ? v : { ...v }])
    ),
    // Блоки «Ещё» — тогглы: в форме у каждого есть состояние, по умолчанию видно
    more: Object.fromEntries(MORE_IDS.map((id) => [id, (a.more || {})[id] !== ACCESS_NONE])),
  };
}

/** Чистка пришедшего с формы: чужие ключи и кривые значения не доезжают до базы */
export function sanitizeAccess(raw) {
  if (!raw || typeof raw !== "object") return {};
  const out = {};
  for (const key of ["overview", "finance", "crm"]) {
    if (LEVELS.has(raw[key])) out[key] = raw[key];
  }
  if (raw.alerts === ACCESS_NONE || raw.alerts === ACCESS_READ) out.alerts = raw.alerts;
  const crmBiz = {};
  for (const [bizId, v] of Object.entries(raw.crmBiz || {})) {
    if (typeof v === "string") {
      if (LEVELS.has(v)) crmBiz[bizId] = v;
    } else if (v && typeof v === "object") {
      const streams = {};
      for (const s of CRM_STREAM_KEYS) if (LEVELS.has(v[s])) streams[s] = v[s];
      // Пустой объект хранить незачем — он ничего не переопределяет
      if (Object.keys(streams).length) crmBiz[bizId] = streams;
    }
  }
  if (Object.keys(crmBiz).length) out.crmBiz = crmBiz;
  /* Из формы блоки «Ещё» приходят тогглами, а хранится только скрытое:
     видимый блок — это отсутствие записи, как и у остальных прав */
  const more = {};
  for (const id of MORE_IDS) {
    if (raw.more?.[id] === false || raw.more?.[id] === ACCESS_NONE) more[id] = ACCESS_NONE;
  }
  if (Object.keys(more).length) out.more = more;
  return out;
}
